import React from 'react';
import { removeCountryFromAddress } from '@/utils/addressFormatter';

interface LocationRouteProps {
  pickupLocation: string;
  destination?: string | null;
  onPickupClick?: () => void;
  onDestinationClick?: () => void;
  callStatus?: string; // e.g., 'NEW', 'DISPATCHED', 'ON_SCENE', 'TOWING', 'COMPLETED'
}

const LocationRoute: React.FC<LocationRouteProps> = ({
  pickupLocation,
  destination,
  onPickupClick,
  onDestinationClick,
  callStatus,
}) => {
  const hasDestination = destination && destination.trim() !== '';
  const displayPickupLocation = removeCountryFromAddress(pickupLocation);
  const displayDestination = destination ? removeCountryFromAddress(destination) : null;
  
  // Determine which address should be bold based on call status
  // Ready (NEW) and En Route (DISPATCHED) should focus on pickup
  const isPickupPhase = callStatus === 'NEW' || callStatus === 'DISPATCHED';
  const pickupIsBold = isPickupPhase;
  const destinationIsBold = !isPickupPhase;
  
  // Destination is disabled during pickup phase
  const destinationDisabled = isPickupPhase;

  return (
    <div className="mb-6">
      <div className="flex gap-4">
        {/* Left column - all dots and line */}
        <div className="flex flex-col items-center">
          {/* Starting dot */}
          <div className="w-2 h-2 bg-primary rounded-full opacity-60"></div>
          
          {/* Main line from start to end */}
          <div className={`w-0.5 bg-primary opacity-30 ${
            hasDestination ? 'h-6' : 'h-3'
          }`}></div>
          
          {/* Stop 1 circle */}
          <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-semibold">
            1
          </div>
          
          {/* Line to Stop 2 (if destination exists) */}
          {hasDestination && (
            <>
              <div className="w-0.5 h-8 bg-primary opacity-30"></div>
              {/* Stop 2 circle */}
              <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-semibold">
                2
              </div>
            </>
          )}
        </div>

        {/* Right column - text only */}
        <div className="flex-1 flex flex-col">
          {/* Space above Stop 1 */}
          <div className={` ${
            hasDestination ? 'h-8' : 'h-5'
          }`}></div>
          
          {/* Pickup Location (Stop 1) */}
          <div className="h-6 flex items-center mb-2">
            <div
              className={`flex-1 cursor-pointer hover:opacity-80 transition-opacity ${
                pickupIsBold ? 'opacity-100' : 'opacity-70'
              }`}
              onClick={onPickupClick}
            >
              <p className={`text-sm text-gray-900 break-words ${
                pickupIsBold ? 'font-bold' : 'font-medium'
              }`}>
                {displayPickupLocation}
              </p>
            </div>
          </div>

          {/* Destination (Stop 2) */}
          {hasDestination && (
            <div className="h-6"></div>
          )}
          {hasDestination && (
            <div className="h-6 flex items-center">
              <div
                className={`flex-1 transition-opacity ${
                  destinationDisabled 
                    ? 'cursor-not-allowed opacity-50' 
                    : 'cursor-pointer hover:opacity-80 opacity-100'
                } ${destinationIsBold ? 'opacity-100' : 'opacity-70'}`}
                onClick={() => {
                  if (!destinationDisabled && onDestinationClick) {
                    onDestinationClick();
                  }
                }}
              >
                <p className={`text-sm text-gray-900 break-words ${
                  destinationIsBold ? 'font-bold' : 'font-medium'
                }`}>
                  {displayDestination}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationRoute;

